/* eslint-disable max-lines */
import { useEffect, useMemo, useReducer, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type {
  Segment,
  SegmentAnalysisPortfolioPayload,
  SegmentAnalysisRole,
  SegmentAnalysisRunMode,
  SegmentAnalysisRunPayload,
  SegmentAnalysisView,
  SegmentCreateDraft,
  SegmentCustomerFilter,
  SegmentDetailTab,
  SegmentFilters,
  SegmentPendingChangesPayload,
} from "../types"

import {
  deriveBackendStatus,
  segmentAnalysisKeys,
  useSegmentAnalysisPendingQuery,
  useSegmentAnalysisPortfolioQuery,
} from "./use-segment-analysis-queries"
import {
  createSegmentAnalysisSegment,
  decideSegmentAnalysisChanges,
  fetchSegmentAnalysisRun,
  patchSegmentAnalysisSegment,
  reassignSegmentAnalysisOwner,
  reclassifySegmentAnalysisCompany,
  startSegmentAnalysisRun,
  toggleSegmentAnalysisCompanyFavourite,
  toggleSegmentAnalysisFavourite,
} from "../api/segment-analysis-api"
import { mockSegments } from "../data/mock-segment-analysis-data"
import { DEFAULT_ADDRESSABLE_LINES } from "../types"
import { resolveSegmentPortfolioSource, SEGMENT_DEMO_DATA } from "../utils/demo-mode"
import {
  buildSegment,
  filterSegments,
  getOwnerNames,
  getPortfolioSummary,
  getSegmentReport,
  isSegmentOwner,
  sortOwnedFirst,
} from "../utils/segment-analysis-utils"

export type SegmentWizardData = SegmentCreateDraft

export type SegmentRunModalState = {
  isOpen: boolean
  mode: SegmentAnalysisRunMode | null
  /** False = showing the "Run report?" confirmation; true = the run has been kicked off. */
  started: boolean
}

export type SegmentAnalysisState = {
  role: SegmentAnalysisRole
  persona: string
  selectedSegmentId: string | null
  detailTab: SegmentDetailTab
  /** Customers-tab classification filter (§5.2 / G2). */
  customerFilter: SegmentCustomerFilter
  /** Free-text name search shared by the Customers + Competitors tabs (§5.2 / G2). */
  entitySearch: string
  filters: SegmentFilters
  showTotal: boolean
  isWizardOpen: boolean
  wizardStep: number
  wizardData: SegmentWizardData
  flashChanges: boolean
  localSegments: Segment[]
  toastMessage: string | null
  runModal: SegmentRunModalState
  useMockData: boolean
}

export type SegmentAnalysisAction =
  | { type: "setRole"; role: SegmentAnalysisRole }
  | { type: "setUseMockData"; useMockData: boolean }
  | { type: "setOwner"; owner: string }
  /** `toggle` marks a hero-card click: re-clicking the selected card clears
   * (or first closes an open dossier) — handled by the module dispatch wrapper. */
  | { type: "selectSegment"; segmentId: string; toggle?: boolean }
  | { type: "clearSelection" }
  | { type: "setDetailTab"; tab: SegmentDetailTab }
  | { type: "setCustomerFilter"; value: SegmentCustomerFilter }
  | { type: "setEntitySearch"; value: string }
  | { type: "setSearch"; value: string }
  | { type: "toggleFilter"; key: "market" | "owner"; value: string }
  | { type: "clearFilter"; key: keyof SegmentFilters }
  | { type: "clearAllFilters" }
  /** Replace the whole filter set — used to restore sticky filters on load (#2068 C2). */
  | { type: "setFilters"; filters: SegmentFilters }
  | { type: "toggleFavouritesFilter" }
  | { type: "toggleTotalLine" }
  | { type: "openWizard" }
  | { type: "closeWizard" }
  | { type: "wizardBack" }
  | { type: "wizardNext" }
  | { type: "setWizardField"; field: "name" | "market" | "description"; value: string }
  | {
      type: "addWizardTag"
      field: "keywords" | "customers" | "competitors" | "sources"
      value: string
    }
  | {
      type: "removeWizardTag"
      field: "keywords" | "customers" | "competitors" | "sources"
      index: number
    }
  | { type: "toggleWizardGeo"; geo: string }
  | { type: "createSegment"; draft: SegmentCreateDraft }
  | { type: "openRunModal"; mode: SegmentAnalysisRunMode }
  | { type: "markRunStarted" }
  | { type: "closeRunModal" }
  | { type: "finishRun"; mode: SegmentAnalysisRunMode }
  | { type: "clearToast" }
  | { type: "showToast"; message: string }
  | { type: "clearFlash" }

const emptyWizardData: SegmentWizardData = {
  name: "",
  market: "",
  description: "",
  keywords: [],
  geo: [],
  customers: [],
  competitors: [],
  sources: [],
}

const emptyFilters: SegmentFilters = {
  market: [],
  owner: [],
  search: "",
  favouritesOnly: false,
}

const EMPTY_SEGMENTS: Segment[] = []

function allSegmentsForState(state: SegmentAnalysisState): Segment[] {
  return [...mockSegments, ...state.localSegments]
}

function firstSegmentForOwner(segments: Segment[], owner: string): string | null {
  return segments.find((segment) => segment.owner === owner)?.id ?? null
}

function selectedSegmentBelongsToOwner(state: SegmentAnalysisState, owner: string): boolean {
  return allSegmentsForState(state).some(
    (segment) => segment.id === state.selectedSegmentId && segment.owner === owner
  )
}

export function createInitialSegmentAnalysisState(): SegmentAnalysisState {
  return {
    role: "admin",
    persona: "Luca Moretti",
    selectedSegmentId: "seg-coffee-machines",
    detailTab: "market",
    customerFilter: "all",
    entitySearch: "",
    filters: { ...emptyFilters },
    showTotal: false,
    isWizardOpen: false,
    wizardStep: 0,
    wizardData: { ...emptyWizardData },
    flashChanges: false,
    localSegments: [],
    toastMessage: null,
    runModal: {
      isOpen: false,
      mode: null,
      started: false,
    },
    useMockData: false,
  }
}

export function segmentAnalysisReducer(
  state: SegmentAnalysisState,
  action: SegmentAnalysisAction
): SegmentAnalysisState {
  switch (action.type) {
    case "setRole": {
      if (action.role === "admin") {
        return {
          ...state,
          role: "admin",
          filters: {
            ...state.filters,
            owner: [],
          },
        }
      }

      return {
        ...state,
        role: "owner",
        filters: {
          ...state.filters,
          owner: [state.persona],
        },
        selectedSegmentId: selectedSegmentBelongsToOwner(state, state.persona)
          ? state.selectedSegmentId
          : firstSegmentForOwner(allSegmentsForState(state), state.persona),
      }
    }
    case "setUseMockData":
      return {
        ...state,
        useMockData: action.useMockData,
      }
    case "setOwner":
      return {
        ...state,
        role: "owner",
        persona: action.owner,
        filters: {
          ...state.filters,
          owner: [action.owner],
        },
        selectedSegmentId: selectedSegmentBelongsToOwner(state, action.owner)
          ? state.selectedSegmentId
          : firstSegmentForOwner(allSegmentsForState(state), action.owner),
      }
    case "selectSegment":
      return {
        ...state,
        selectedSegmentId: action.segmentId,
        detailTab: "market",
        // Fresh segment → reset the per-segment customer/competitor view filters.
        customerFilter: "all",
        entitySearch: "",
        flashChanges: false,
      }
    case "clearSelection":
      return {
        ...state,
        selectedSegmentId: null,
        customerFilter: "all",
        entitySearch: "",
        flashChanges: false,
      }
    case "setDetailTab":
      // Switching tabs clears the shared entity search so it doesn't bleed between
      // the Customers and Competitors lists.
      return {
        ...state,
        detailTab: action.tab,
        entitySearch: "",
      }
    case "setCustomerFilter":
      return {
        ...state,
        customerFilter: action.value,
      }
    case "setEntitySearch":
      return {
        ...state,
        entitySearch: action.value,
      }
    case "setSearch":
      return {
        ...state,
        filters: {
          ...state.filters,
          search: action.value,
        },
      }
    case "toggleFilter": {
      const current = state.filters[action.key]
      const next = current.includes(action.value as never)
        ? current.filter((value) => value !== action.value)
        : [...current, action.value]

      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: next,
        },
      }
    }
    case "clearFilter": {
      let clearedValue: string | boolean | string[] = []
      if (action.key === "search") clearedValue = ""
      else if (action.key === "favouritesOnly") clearedValue = false
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.key]: clearedValue,
        },
      }
    }
    case "toggleFavouritesFilter":
      return {
        ...state,
        filters: {
          ...state.filters,
          favouritesOnly: !state.filters.favouritesOnly,
        },
      }
    case "clearAllFilters":
      return {
        ...state,
        filters: { ...emptyFilters },
      }
    case "setFilters":
      return {
        ...state,
        filters: { ...action.filters },
      }
    case "toggleTotalLine":
      return {
        ...state,
        showTotal: !state.showTotal,
      }
    case "openWizard":
      return {
        ...state,
        isWizardOpen: true,
        wizardStep: 0,
        wizardData: { ...emptyWizardData },
      }
    case "closeWizard":
      return {
        ...state,
        isWizardOpen: false,
      }
    case "wizardBack":
      return {
        ...state,
        wizardStep: Math.max(0, state.wizardStep - 1),
      }
    case "wizardNext":
      return {
        ...state,
        wizardStep: Math.min(4, state.wizardStep + 1),
      }
    case "setWizardField":
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          [action.field]: action.value,
        },
      }
    case "addWizardTag": {
      const value = action.value.trim()
      if (!value || state.wizardData[action.field].includes(value)) return state

      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          [action.field]: [...state.wizardData[action.field], value],
        },
      }
    }
    case "removeWizardTag":
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          [action.field]: state.wizardData[action.field].filter(
            (_, index) => index !== action.index
          ),
        },
      }
    case "toggleWizardGeo":
      return {
        ...state,
        wizardData: {
          ...state.wizardData,
          geo: state.wizardData.geo.includes(action.geo)
            ? state.wizardData.geo.filter((geo) => geo !== action.geo)
            : [...state.wizardData.geo, action.geo],
        },
      }
    case "createSegment": {
      const draft = action.draft
      const name = draft.name || "Untitled segment"
      const nextSegment = buildSegment({
        id: `seg-new-${state.localSegments.length + 1}`,
        name,
        marketName: draft.market && draft.market !== "__new__" ? draft.market : "General Industry",
        owner: state.role === "owner" ? state.persona : "Unassigned",
        description: draft.description,
        customers: draft.customers,
        competitors: draft.competitors,
      })

      return {
        ...state,
        localSegments: [...state.localSegments, nextSegment],
        selectedSegmentId: nextSegment.id,
        detailTab: "market",
        isWizardOpen: false,
        wizardStep: 0,
        wizardData: { ...emptyWizardData },
        toastMessage: `Segment "${name}" created - baseline report scheduled.`,
      }
    }
    case "openRunModal":
      // Open in the confirmation state — the run isn't kicked off until the user
      // confirms (markRunStarted), so a stray click can't commit a backend run.
      return {
        ...state,
        runModal: {
          isOpen: true,
          mode: action.mode,
          started: false,
        },
      }
    case "markRunStarted":
      return {
        ...state,
        runModal: {
          ...state.runModal,
          started: true,
        },
      }
    case "closeRunModal":
      return {
        ...state,
        runModal: {
          isOpen: false,
          mode: null,
          started: false,
        },
      }
    case "finishRun":
      return {
        ...state,
        runModal: {
          isOpen: false,
          mode: null,
          started: false,
        },
        detailTab: "market",
        flashChanges: true,
        selectedSegmentId: state.selectedSegmentId ?? "seg-coffee-machines",
        toastMessage:
          action.mode === "partial"
            ? "Partial report updated - 3 changes since last run."
            : "Full report updated - 3 changes since last run.",
      }
    case "clearToast":
      return {
        ...state,
        toastMessage: null,
      }
    case "showToast":
      return {
        ...state,
        toastMessage: action.message,
      }
    case "clearFlash":
      return {
        ...state,
        flashChanges: false,
      }
    default:
      return state
  }
}

export function mergeCompletedSegmentAnalysisRun(
  payload: SegmentAnalysisPortfolioPayload,
  run: SegmentAnalysisRunPayload
): SegmentAnalysisPortfolioPayload {
  if (run.status !== "completed" || !run.report) return payload

  return {
    ...payload,
    generatedAt: run.completedAt ?? run.updatedAt,
    reports: {
      ...payload.reports,
      [run.segmentId]: run.report,
    },
    segments: payload.segments.map((segment) =>
      segment.id === run.segmentId
        ? {
            ...segment,
            lastRunDate: (run.completedAt ?? run.updatedAt).slice(0, 10),
            runStatus: "ok",
          }
        : segment
    ),
  }
}

function resolveBackendError(error: unknown): string | null {
  if (!error) return null
  return error instanceof Error ? error.message : "Unable to load backend portfolio"
}

/**
 * Resolves the segment shown in the detail pane against the *filtered* view
 * (Nolato feedback #2 / #3295). A market/owner filter change can drop the
 * currently selected segment out of `visibleSegments`; it must not linger in
 * the detail section once it's no longer visible — fall back to the first
 * visible segment, or the empty state (`null`) if none remain.
 *
 * An explicitly cleared selection (`null`, e.g. the hero-card toggle-off
 * gesture from #2063) stays cleared: it must not snap to the first segment.
 */
export function resolveSelectedSegment(
  selectedSegmentId: string | null,
  visibleSegments: Segment[]
): Segment | null {
  if (!selectedSegmentId) return null

  const matchingVisibleSegment = visibleSegments.find((segment) => segment.id === selectedSegmentId)
  if (matchingVisibleSegment) return matchingVisibleSegment

  return visibleSegments[0] ?? null
}

/**
 * True when `action` is the hero-card toggle-off gesture (#2063): a toggle click
 * on the segment the user currently sees as selected. Compares against the
 * *resolved* selection (`resolveSelectedSegment`), not the raw state id, so after
 * a filter hides the selected segment and the detail pane falls back to another
 * one, re-clicking that highlighted card still clears the selection.
 */
export function isSelectionToggleOff(
  action: SegmentAnalysisAction,
  selectedSegment: Segment | null
): boolean {
  return (
    action.type === "selectSegment" &&
    Boolean(action.toggle) &&
    selectedSegment !== null &&
    action.segmentId === selectedSegment.id
  )
}

export function useSegmentAnalysis(view: SegmentAnalysisView) {
  const [state, dispatch] = useReducer(
    segmentAnalysisReducer,
    undefined,
    createInitialSegmentAnalysisState
  )
  const [currentRun, setCurrentRun] = useState<SegmentAnalysisRunPayload | null>(null)
  const queryClient = useQueryClient()

  // ── Read layer (react-query) ────────────────────────────────────────────
  // The portfolio query is the source of truth for the live-backend path; it is
  // disabled in mock mode so no network request fires. backendStatus/backendError
  // are derived from its state to preserve the old idle/loading/error/ready
  // semantics. The pending query is wired up further down, once selectedSegmentId
  // and dataSource are resolved.
  const portfolioQuery = useSegmentAnalysisPortfolioQuery(!state.useMockData)
  const backendPayload = portfolioQuery.data ?? null
  const backendStatus = deriveBackendStatus(portfolioQuery)
  const backendError = resolveBackendError(portfolioQuery.error)

  // Invalidation helpers — the canonical map for every write path (req 3):
  //   run-complete + decide → portfolio + that segment's pending
  //   create / favourite / company-favourite → portfolio
  const invalidatePortfolio = () =>
    queryClient.invalidateQueries({ queryKey: segmentAnalysisKeys.portfolio() })
  const invalidatePending = (segmentId: string) =>
    queryClient.invalidateQueries({ queryKey: segmentAnalysisKeys.pending(segmentId) })

  const backendSegments = backendPayload?.segments ?? EMPTY_SEGMENTS
  // What backs the portfolio — see resolveSegmentPortfolioSource (#2768): demo
  // tenants fall back to the mock portfolio when the backend has no segments;
  // live-only tenants treat a ready-but-empty backend as the (empty) portfolio.
  const { usesBackendPortfolio, fallback, dataSource } = resolveSegmentPortfolioSource({
    useMockData: state.useMockData,
    backendStatus,
    backendSegmentCount: backendSegments.length,
    demoDataEnabled: SEGMENT_DEMO_DATA,
  })
  const fallbackSegments = fallback === "demo" ? mockSegments : EMPTY_SEGMENTS
  const portfolioSegments = usesBackendPortfolio ? backendSegments : fallbackSegments

  const currentUserOid = backendPayload?.currentUserOid ?? null
  const currentUserName = backendPayload?.currentUserName ?? null

  const allSegments = useMemo(
    () => [...portfolioSegments, ...state.localSegments],
    [portfolioSegments, state.localSegments]
  )
  const visibleSegments = useMemo(
    () =>
      sortOwnedFirst(filterSegments(allSegments, state.filters, currentUserOid), currentUserOid),
    [allSegments, state.filters, currentUserOid]
  )
  // Companies/Map derive from Market + Owner only (#3306 #7 review finding 2):
  // the Portfolio-only search box and segment-level Favourites filter must not
  // silently narrow those views, since their filter bar hides those controls
  // (`marketAndOwnerOnly`) and has no way to reveal or clear them.
  const marketOwnerFilteredSegments = useMemo(
    () =>
      sortOwnedFirst(
        filterSegments(
          allSegments,
          { ...state.filters, search: "", favouritesOnly: false },
          currentUserOid
        ),
        currentUserOid
      ),
    [allSegments, state.filters, currentUserOid]
  )
  // Resolution scope depends on the active view (#3311 review finding 3):
  // Companies/Map feed selection from a node/row in marketOwnerFilteredSegments
  // (#3306 #7 review finding 1), and a Portfolio-only search or Favourites
  // filter must not be able to make that selection resolve to the WRONG
  // segment (falling back to marketOwnerFilteredSegments[0]) or linger once
  // it's dropped by an actual Market/Owner change. Portfolio, though, keeps
  // resolving against visibleSegments — its OWN search/Favourites filter must
  // still fall back to the first visible card (or the empty detail pane) when
  // it hides the selection, exactly as it did before #3306.
  const selectionScopeSegments =
    view === "portfolio" ? visibleSegments : marketOwnerFilteredSegments
  const selectedSegment = useMemo(
    () => resolveSelectedSegment(state.selectedSegmentId, selectionScopeSegments),
    [state.selectedSegmentId, selectionScopeSegments]
  )
  const selectedSegmentId = selectedSegment?.id ?? null
  const report = useMemo(() => {
    if (!selectedSegmentId) return null

    if (dataSource === "backend") {
      return backendPayload?.reports[selectedSegmentId] ?? null
    }

    return getSegmentReport(selectedSegmentId) ?? backendPayload?.reports[selectedSegmentId] ?? null
  }, [backendPayload?.reports, dataSource, selectedSegmentId])
  const claimRegistry =
    selectedSegmentId && dataSource === "backend"
      ? (backendPayload?.claimRegistries?.[selectedSegmentId] ?? null)
      : null
  const owners = useMemo(() => getOwnerNames(allSegments), [allSegments])
  const summary = useMemo(() => getPortfolioSummary(visibleSegments), [visibleSegments])
  // KPI totals for the Companies/Map views (#3306 #7 review finding 2): those
  // views render marketOwnerFilteredSegments, so their KPI row above must sum
  // the same set — never `summary`, which still carries the Portfolio-only
  // search/Favourites narrowing those views have no control to see or clear.
  const marketOwnerSummary = useMemo(
    () => getPortfolioSummary(marketOwnerFilteredSegments),
    [marketOwnerFilteredSegments]
  )

  // Tenant-merged addressable-opportunity lines (G8), layer 2 of the config model.
  // Fall back to the default lines whenever the payload omits presentation
  // (mock mode, an old/cached response, or a fetch failure) so the KPI row is never
  // empty. The per-user layer (G9) will be applied on top of this client-side.
  const addressableLines = useMemo(
    () => backendPayload?.presentation?.addressableLines ?? DEFAULT_ADDRESSABLE_LINES,
    [backendPayload?.presentation]
  )

  // Role resolution (§5.7): in mock/demo mode the header toggle drives the role;
  // against the backend the server-resolved role is authoritative.
  const backendRole = backendPayload?.viewerRole ?? null
  const effectiveRole: SegmentAnalysisRole =
    state.useMockData || dataSource !== "backend" ? state.role : (backendRole ?? state.role)
  const canCreateSegment = effectiveRole === "admin"
  const canEditSelectedSegment = useMemo(() => {
    if (effectiveRole === "admin") return true
    if (effectiveRole === "viewer") return false
    if (!selectedSegment) return false
    // Owner: only segments they own. Legacy/seed segments carry no ownerOid, so
    // we stay permissive there to match the backend (pre-F3 data parity).
    return !selectedSegment.ownerOid || selectedSegment.ownerOid === currentUserOid
  }, [effectiveRole, selectedSegment, currentUserOid])
  // Manual sales / addressable pens: segment owner only (not admin/`canRun`).
  const canEditManualMetrics = useMemo(() => {
    if (!selectedSegment) return false
    const nameForOwnership =
      state.useMockData || dataSource !== "backend" ? state.persona : currentUserName
    return isSegmentOwner(selectedSegment, currentUserOid, nameForOwnership)
  }, [
    selectedSegment,
    currentUserOid,
    currentUserName,
    state.useMockData,
    state.persona,
    dataSource,
  ])

  useEffect(() => {
    if (state.useMockData) {
      setCurrentRun(null)
    }
  }, [state.useMockData])

  useEffect(() => {
    if (!currentRun || !state.runModal.isOpen) return
    if (!["queued", "processing"].includes(currentRun.status)) return

    let isActive = true
    const timer = window.setTimeout(() => {
      fetchSegmentAnalysisRun(currentRun.runId)
        .then((run) => {
          if (!isActive) return

          setCurrentRun(run)
          if (run.status === "completed") {
            // Completed runs do NOT go live; they surface as pending changes
            // for human approval (§5.4). Refresh portfolio (pending counts) + pending.
            void invalidatePortfolio()
            void invalidatePending(run.segmentId)
          }
        })
        .catch((error: unknown) => {
          if (!isActive) return

          const message =
            error instanceof Error ? error.message : "Unable to refresh Segment Analysis run"
          setCurrentRun({
            ...currentRun,
            error: message,
            status: "failed",
            updatedAt: new Date().toISOString(),
          })
        })
    }, 1400)

    return () => {
      isActive = false
      window.clearTimeout(timer)
    }
  }, [currentRun, state.runModal.isOpen])

  // Load pending changes for the selected segment (backend only). The query is
  // keyed by segmentId; disabling it in mock/non-backend mode stops fetches but
  // leaves any stale cache entry intact, so we gate the *returned* value below.
  const usesBackendPending = !state.useMockData && dataSource === "backend"
  const pendingQuery = useSegmentAnalysisPendingQuery(selectedSegmentId, usesBackendPending)
  const pendingChanges: SegmentPendingChangesPayload | null = usesBackendPending
    ? (pendingQuery.data ?? null)
    : null

  const decidePendingChange = async (decision: "approve" | "reject", changeId?: string) => {
    if (!selectedSegmentId || state.useMockData || dataSource !== "backend") return
    try {
      await decideSegmentAnalysisChanges(selectedSegmentId, { decision, changeId })
      await Promise.all([invalidatePortfolio(), invalidatePending(selectedSegmentId)])
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to apply decision",
      })
    }
  }

  // Opening the run modal only shows a confirmation — a research run is expensive
  // and (on the backend) creates pending changes, so it must not fire on one click.
  // The actual run is kicked off by confirmRun() when the user confirms.
  const startRun = (mode: SegmentAnalysisRunMode) => {
    if (!selectedSegmentId) return
    dispatch({ type: "openRunModal", mode })
  }

  const confirmRun = async (options?: { modelId?: string; reasoningEffort?: string }) => {
    const mode = state.runModal.mode
    if (!selectedSegmentId || !mode) return

    dispatch({ type: "markRunStarted" })

    if (state.useMockData) {
      setCurrentRun(null)
      return
    }

    try {
      const run = await startSegmentAnalysisRun({
        mode,
        segmentId: selectedSegmentId,
        ...(options?.modelId ? { modelId: options.modelId } : {}),
        ...(options?.reasoningEffort ? { reasoningEffort: options.reasoningEffort } : {}),
      })
      setCurrentRun(run)
      if (run.status === "completed") {
        void invalidatePortfolio()
        void invalidatePending(run.segmentId)
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Unable to start Segment Analysis run"
      setCurrentRun({
        activities: [
          {
            id: "start-failed",
            message,
            status: "failed",
          },
        ],
        createdAt: new Date().toISOString(),
        error: message,
        mode,
        report: null,
        runId: "run-start-failed",
        segmentId: selectedSegmentId,
        sourceCount: 0,
        sources: [],
        status: "failed",
        updatedAt: new Date().toISOString(),
      })
    }
  }

  const usesBackend = !state.useMockData && dataSource === "backend"

  const createSegment = async (draft: SegmentCreateDraft) => {
    if (!usesBackend) {
      dispatch({ type: "createSegment", draft })
      return
    }
    try {
      const segment = await createSegmentAnalysisSegment(draft)
      dispatch({ type: "closeWizard" })
      await invalidatePortfolio()
      dispatch({ type: "selectSegment", segmentId: segment.id })
      dispatch({
        type: "showToast",
        message: `Segment "${segment.name}" created - baseline report scheduled.`,
      })
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to create segment",
      })
    }
  }

  const toggleFavourite = async (segmentId: string) => {
    if (!usesBackend) return
    try {
      await toggleSegmentAnalysisFavourite(segmentId)
      await invalidatePortfolio()
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to update favourite",
      })
    }
  }

  // The current user's favourited company names within the selected segment (§5.2/§5.8).
  // Per-user, read from the segment record; falls back to empty when absent or in mock.
  const favouriteCompanies = useMemo(
    () =>
      new Set(
        currentUserOid && selectedSegment?.favouriteCompaniesBy
          ? (selectedSegment.favouriteCompaniesBy[currentUserOid] ?? [])
          : []
      ),
    [currentUserOid, selectedSegment]
  )

  /** Toggle within an explicit segment (#2072 — the company directory favourites
   * across segments) or, by default, the selected one. */
  const toggleCompanyFavourite = async (companyName: string, segmentId?: string) => {
    const targetSegmentId = segmentId ?? selectedSegmentId
    if (!usesBackend || !targetSegmentId) return
    try {
      await toggleSegmentAnalysisCompanyFavourite(targetSegmentId, companyName)
      await invalidatePortfolio()
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to update favourite",
      })
    }
  }

  // Admin-only ownership transfer (§5.7). Owner is otherwise frozen at creation;
  // this redirects who is the approver of record, so it's gated to admins.
  const canReassignOwner = effectiveRole === "admin"
  const reassignOwner = async (segmentId: string, ownerName: string, ownerOid?: string | null) => {
    if (!usesBackend) return
    try {
      const segment = await reassignSegmentAnalysisOwner(segmentId, { ownerName, ownerOid })
      await invalidatePortfolio()
      dispatch({ type: "showToast", message: `Owner of "${segment.name}" set to ${ownerName}.` })
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to reassign owner",
      })
    }
  }

  const reclassifyCompany = async (
    segmentId: string,
    companyName: string,
    next: "customer" | "prospect"
  ) => {
    if (!usesBackend) return
    try {
      const result = await reclassifySegmentAnalysisCompany(segmentId, {
        companyName,
        classification: next,
      })
      await Promise.all([invalidatePortfolio(), invalidatePending(segmentId)])
      dispatch({
        type: "showToast",
        message:
          result.outcome === "pending"
            ? `${companyName} classification is pending approval.`
            : `${companyName} marked as ${next}.`,
      })
    } catch (error: unknown) {
      await Promise.all([invalidatePortfolio(), invalidatePending(segmentId)])
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to reclassify company",
      })
    }
  }

  const updateManualMetrics = async (
    segmentId: string,
    input: { sales?: number; addressable?: Record<string, number> }
  ) => {
    if (!usesBackend) {
      dispatch({ type: "showToast", message: "Manual metrics require the live backend." })
      return
    }
    try {
      await patchSegmentAnalysisSegment(segmentId, input)
      await invalidatePortfolio()
      dispatch({ type: "showToast", message: "Sales and addressable market updated." })
    } catch (error: unknown) {
      dispatch({
        type: "showToast",
        message: error instanceof Error ? error.message : "Unable to update metrics",
      })
      throw error
    }
  }

  return {
    state,
    dispatch,
    allSegments,
    visibleSegments,
    marketOwnerFilteredSegments,
    selectedSegment,
    report,
    owners,
    summary,
    marketOwnerSummary,
    backendError,
    backendGeneratedAt: backendPayload?.generatedAt ?? null,
    backendStatus,
    currentRun,
    dataSource,
    startRun,
    confirmRun,
    effectiveRole,
    currentUserOid,
    canCreateSegment,
    canEditSelectedSegment,
    canEditManualMetrics,
    canReassignOwner,
    claimRegistry,
    reassignOwner,
    reclassifyCompany,
    updateManualMetrics,
    createSegment,
    toggleFavourite,
    favouriteCompanies,
    toggleCompanyFavourite,
    canFavouriteCompany: usesBackend,
    pendingChanges,
    pendingCounts: backendPayload?.pendingCounts ?? {},
    decidePendingChange,
    addressableLines,
  }
}
